import { ArrowUpRight, Quote, Star } from "lucide-react";

const CASES = [
  { company: "Global CPG Leader", industry: "Finance", metric: "45%", metricLabel: "faster close", title: "Automating reconciliation across 12 ERPs" },
  { company: "Fortune 100 Bank", industry: "Financial Services", metric: "3x", metricLabel: "faster reconciliation", title: "Trusted context for finance ops" },
  { company: "Industrial Manufacturer", industry: "Supply Chain", metric: "20%", metricLabel: "lower inventory cost", title: "Smarter demand & inventory planning" },
  { company: "Healthcare Network", industry: "Operations", metric: "60%", metricLabel: "less manual reporting", title: "Always-current operational reporting" },
  { company: "Global Retail Group", industry: "Marketing", metric: "2x", metricLabel: "campaign throughput", title: "Orchestrating campaigns end-to-end" },
  { company: "Telecom Provider", industry: "Customer Service", metric: "35%", metricLabel: "faster resolution", title: "From case management to resolution" },
];

const Tag = ({ children, gold }) => <span className={`font-mono2 text-[10px] uppercase tracking-[0.2em] ${gold ? "text-[#E8AA68]" : "text-[#8A5730]"}`}>{children}</span>;

/* E · Dark immersive cards with gold glow */
const E = () => (
  <div className="rounded-2xl bg-[#211307] p-6 lg:p-8">
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {CASES.map((c) => (
        <div key={c.company} className="group relative rounded-xl p-[1px] bg-gradient-to-b from-[#E8AA68]/40 to-white/5 hover:from-[#E8AA68] transition-colors duration-300">
          <div className="rounded-xl bg-[#2A1809] h-full p-5 flex flex-col">
            <Tag gold>{c.industry}</Tag>
            <div className="mt-4 font-display text-4xl font-medium text-[#F7F5F0]">{c.metric}</div>
            <div className="text-[12px] text-[#E8AA68]">{c.metricLabel}</div>
            <p className="text-[14px] font-semibold text-white/90 mt-3 leading-snug">{c.title}</p>
            <div className="mt-auto pt-4 flex items-center justify-between">
              <span className="text-[12px] text-white/55">{c.company}</span>
              <ArrowUpRight size={15} className="text-[#E8AA68] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* F · Big-number editorial */
const F = () => (
  <div className="divide-y divide-[#3E2818]/12 border-y border-[#3E2818]/12">
    {CASES.slice(0, 5).map((c) => (
      <div key={c.company} className="group grid lg:grid-cols-[0.32fr_1fr_auto] gap-4 lg:gap-10 items-center py-7 hover:px-2 transition-all duration-300">
        <div className="font-display text-5xl lg:text-6xl font-medium text-[#3E2818] leading-none">{c.metric}<span className="block font-mono2 text-[11px] tracking-[0.15em] text-[#8A5730] mt-2 uppercase">{c.metricLabel}</span></div>
        <div>
          <Tag>{c.industry} · {c.company}</Tag>
          <p className="font-display text-xl lg:text-2xl font-medium text-[#3E2818] mt-1.5 leading-snug">{c.title}</p>
        </div>
        <span className="w-11 h-11 rounded-full border border-[#3E2818]/20 flex items-center justify-center text-[#8A5730] group-hover:bg-[#211307] group-hover:text-[#E8AA68] group-hover:border-[#211307] transition-colors duration-300"><ArrowUpRight size={18} /></span>
      </div>
    ))}
  </div>
);

/* G · Testimonial spotlight */
const G = () => (
  <div className="rounded-2xl bg-white border border-[#3E2818]/10 overflow-hidden grid lg:grid-cols-[1.3fr_1fr]">
    <div className="p-8 lg:p-10 flex flex-col justify-between">
      <div>
        <div className="flex gap-1 mb-5">{[...Array(5)].map((_, i) => <Star key={i} size={15} className="fill-[#EDA13A] text-[#EDA13A]" />)}</div>
        <Quote size={30} className="text-[#8A5730]/30" />
        <p className="font-display text-xl lg:text-2xl font-medium text-[#3E2818] leading-snug mt-3">Intugle became the trusted context layer behind every agent we deploy — our teams finally work from one source of truth.</p>
      </div>
      <div className="mt-8 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#211307] text-[#E8AA68] flex items-center justify-center font-display font-medium">CT</div>
        <div><p className="text-[13px] font-semibold text-[#3E2818]">Chief Data Officer</p><p className="text-[12px] text-[#3E2818]/55">Fortune 100 Bank</p></div>
      </div>
    </div>
    <div className="bg-[#211307] p-8 lg:p-10 flex flex-col justify-center gap-6">
      {[{ m: "3x", l: "faster reconciliation" }, { m: "40+", l: "data sources unified" }, { m: "60%", l: "less manual effort" }].map((s) => (
        <div key={s.l}>
          <div className="font-display text-4xl font-medium text-[#E8AA68]">{s.m}</div>
          <div className="text-[12px] text-white/60">{s.l}</div>
        </div>
      ))}
    </div>
  </div>
);

/* H · Cover-tile cards with metric badge overlay */
const H = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {CASES.map((c, i) => (
      <div key={c.company} className="group rounded-xl overflow-hidden border border-[#3E2818]/10 bg-white hover:-translate-y-0.5 hover:shadow-[0_22px_50px_-30px_rgba(62,40,24,0.5)] transition-all duration-300">
        <div className={`relative h-36 ${["bg-gradient-to-br from-[#8A5730] to-[#3E2818]", "bg-gradient-to-br from-[#EDA13A] to-[#8A5730]", "bg-gradient-to-br from-[#2E1B0C] to-[#5A3418]"][i % 3]}`}>
          <span className="absolute top-3 left-3"><Tag gold>{c.industry}</Tag></span>
          <div className="absolute bottom-3 left-3 text-white">
            <div className="font-display text-3xl font-medium">{c.metric}</div>
            <div className="text-[11px] text-white/80">{c.metricLabel}</div>
          </div>
        </div>
        <div className="p-4">
          <p className="text-[14px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
          <div className="mt-3 flex items-center justify-between"><span className="text-[12px] text-[#3E2818]/60">{c.company}</span><ArrowUpRight size={15} className="text-[#8A5730]" /></div>
        </div>
      </div>
    ))}
  </div>
);

/* I · Hover-expand panels */
const I = () => (
  <div className="flex flex-col lg:flex-row gap-3 lg:h-[320px]">
    {CASES.slice(0, 5).map((c, idx) => (
      <div key={c.company} className={`group relative rounded-xl overflow-hidden bg-[#211307] text-[#F7F5F0] p-5 flex flex-col justify-end transition-all duration-500 lg:flex-[1] lg:hover:flex-[3] cursor-pointer ${idx === 0 ? "lg:flex-[3]" : ""}`}>
        <div className={`absolute inset-0 ${["bg-gradient-to-t from-[#211307] via-[#211307]/60 to-[#5A3418]/40", "bg-gradient-to-t from-[#211307] to-[#3E2818]"][idx % 2]}`} />
        <div className="relative">
          <Tag gold>{c.industry}</Tag>
          <div className="font-display text-4xl font-medium text-[#E8AA68] mt-2">{c.metric}</div>
          <p className="text-[13px] font-semibold mt-1 opacity-90 transition-opacity duration-300">{c.title}</p>
          <p className="text-[12px] text-white/60 mt-1">{c.company}</p>
        </div>
      </div>
    ))}
  </div>
);

const OPTIONS = [
  { id: "e", title: "Option E · Dark Immersive Cards", note: "A dark espresso section with gold-glow gradient borders that light up on hover. Creates a premium, high-contrast moment and a light→dark rhythm on the page.", comp: E },
  { id: "f", title: "Option F · Big-Number Editorial", note: "Oversized result numbers as the hero of each row. Bold, confident and very high-end — the metric does the talking.", comp: F },
  { id: "g", title: "Option G · Testimonial Spotlight", note: "A flagship quote with star rating and a dark stats panel. Leads with social proof — powerful for trust and conversion.", comp: G },
  { id: "h", title: "Option H · Cover-Tile Cards", note: "Magazine-style cards with a colored cover, a metric badge overlaid, and the story below. Visual and brandable (covers can become real imagery/logos).", comp: H },
  { id: "i", title: "Option I · Hover-Expand Panels", note: "Interactive accordion panels that expand on hover to reveal each story. Distinctive and dynamic — a real showpiece.", comp: I },
];

export default function CaseStudyOptions2() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Success Stories · Bolder Templates</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">A more compelling set</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Rendered with placeholder stories. Each scales to 8–10 case studies. Tell me the letter.</p>

        <div className="mt-12 space-y-16">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-4">{o.title}</p>
                <C />
                <p className="text-[12px] text-[#3E2818]/55 mt-4 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
